import UpshowLogger from '../Logger';
import SettingsService from './SettingsService';
import { fetchAndApplyLiveEvents } from '../Upshow';

const RETRY_DELAYS = [5_000, 10_000, 30_000, 60_000];

class LiveEventsSyncService {
    _running = false;
    _generation = 0;
    _retryCount = 0;
    _socket = null;
    _listeners = null;

    // Called once from index.js, before connectToSocket, so the handlers are in
    // place before the first connect event fires.
    register (socket) {
        if (this._listeners) this._deregister();

        this._socket = socket;
        this._listeners = {
            connect: () => this._onConnect(),
            disconnect: () => this._onDisconnect()
        };

        this._socket.on('connect', this._listeners.connect);
        this._socket.on('disconnect', this._listeners.disconnect);

        if (this._socket.connected) this._onConnect();
    }

    _deregister () {
        if (this._socket && this._listeners) {
            this._socket.off('connect', this._listeners.connect);
            this._socket.off('disconnect', this._listeners.disconnect);
        }
        this._listeners = null;
        this._socket = null;
    }

    // Called from applyNewState() each time /state is applied (same callsite as
    // PrometheusService.sync). Starts the fallback poll only when the socket is
    // not connected — if it is, pushes are already covering updates.
    start () {
        if (this._running || !SettingsService.hasFeature('live_events')) return;
        if (this._socket?.connected) return;

        this._running = true;
        const interval = SettingsService.getUiSetting('live_events_sync_interval') ?? 5_000;
        // Capture generation NOW so stop() + reconnect can't race past the guard.
        // If stop() runs before the timeout fires, the captured generation will
        // be stale and _tick will see generation !== this._generation and bail.
        const generation = this._generation;
        // Half an interval out of phase so it interleaves with the /state loop.
        setTimeout(() => this._tick(interval, generation), interval / 2);
    }

    stop () {
        this._generation++;
        this._running = false;
    }

    async _tick (interval, generation) {
        if (window.inactive || !SettingsService.hasFeature('live_events') || generation !== this._generation) {
            this._running = false;
            return;
        }

        let delay = window.demo ? 60_000 : interval;

        try {
            await fetchAndApplyLiveEvents();
            this._retryCount = 0;
        } catch (error) {
            UpshowLogger.error(['LiveEventsSyncService'], 'Error synchronizing live events: ' + error);
            delay = RETRY_DELAYS[this._retryCount];
            if (this._retryCount < RETRY_DELAYS.length - 1) this._retryCount++;
        }

        setTimeout(() => this._tick(interval, generation), delay);
    }

    _onConnect () {
        this.stop();
        fetchAndApplyLiveEvents().catch(err =>
            UpshowLogger.error(['LiveEventsSyncService'], 'fetchAndApplyLiveEvents on connect failed: ' + err)
        );
    }

    _onDisconnect () {
        this.start();
    }
}

export default new LiveEventsSyncService();
